import React, { useRef } from "react";
import { Link } from "react-router-dom";

import { Calculator, ArrowUpRight, Rocket } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import BannerCTA from "../CTA/CTABanner.jsx";
import CTAButtonLink from "../CTA/CTAButtonLink.jsx";
import ScrollModal from "../ui/ScrollModal.jsx";
import Seo from "../Seo.jsx";
import guidesConfig from "../../config/guidesConfig";
import Tooltip from "./Tooltip";
import "./guides.css";
import mb1 from "../GetStarted/mb1.png";
import mb2 from "../GetStarted/mb2.png";
import prof1 from './images/profit-easy.png'


const MatchedBettingGuide = () => {
    const scrollRef = useRef(null);
    const meta = guidesConfig.matchedBettingGuide?.seo || {};


  return (
    <div className="guides-content">
            <Seo title={meta.title} description={meta.description} url={guidesConfig.matchedBettingGuide?.route || "/guides"}/>

      <p>
        Welcome to our complete guide to matched betting — the smart way to turn bookmaker offers into real profit. If you’ve ever seen “Bet £10, Get £30 in Free Bets” and wondered if you could make money from it <em>without gambling</em>, this is for you.
      </p>
      <p>
        Matched betting is a strategy used by thousands in the UK, and around the world, every day to make extra income from home. And yes — it’s completely <a href="https://www.oddsmonkey.com/blog/matched-betting/is-matched-betting-legal/" target="_blank" rel="noreferrer">legal</a> and <a href="https://sdlccorp.com/post/what-are-the-tax-implications-of-online-gambling-winnings-in-the-united-kingdom/" target="_blank" rel="noreferrer">tax-free</a> in the UK.
      </p>

      <CTAButtonLink 
        to="/get-started/step1"
        text="Understand How Matched Betting Works in 180 Seconds"
        buttonText="Start now"
        icon={Rocket}
        tabText="Popular"
      />

      <h2>What is Matched Betting?</h2>
      <h6 className="info">Matched betting is a way to turn free offers from betting companies into extra cash by placing bets that cover every outcome to qualify.</h6>
      <p>
        Matched betting is a way to profit from free bets and promotions offered by bookmakers. It works by qualifying for free bets by betting both for and against on the same event that cancel each other out.
      </p>
      <p>
        You then earn a free bet. You do the same again with the free bet to lock in a profit.
      </p>
      <div className="grid-fixed g2">
          <div className="col">
            <img src={mb1} alt="How does matched betting work?" />
          </div>
          <div className="col">
            <h3 className="mt-0">Normal betting</h3>
            <p>When you bet, you bet the bookie that something <em>will</em> happen.</p>
            <ul>
              <li>If you win, the bookie gives you your winnings, and your <Tooltip content={"Your initial bet"}>stake</Tooltip> back.</li>
              <li>If you lose, the bookie keeps your <Tooltip content={"Your initial bet"}>stake</Tooltip>.</li>
            </ul>
          </div>
        </div>
        <div className="grid-fixed g2">
          <div className="col">
            <img src={mb2} alt="How does matched betting work?" />
          </div>
          <div className="col">
            <h3 className="mt-0">Matched betting</h3>
            <p>When you matched bet, you place <em>a second</em> bet <strong>against</strong> the same outcome.</p>
            <p>We do this to <span className="key">qualify</span> for a free bet.</p>
            <p><strong>This time you act like the bookie!</strong></p>
          </div>
        </div>
        <p>These bets cancel each other out. We have earned a free bet, and we follow the <strong>same process</strong> to lock in profit from our free bet.</p>

      <h2>How Does Matched Betting Work?</h2>
      <p>Imagine we are targeting a  Bet £10, Get £30 in free bets offer</p>
      <h3>Step 1: Place a Qualifying Bet</h3>
      <p>
        Place a real-money <span className="back">back</span> bet <em>for</em> an outcome at the bookie.
      </p>
      <h6 className="info">A <span className="back">back</span> bet is a bet that an outcome <em>will</em> happen.</h6>
      <h3>Step 2: Cancel Out the Qualifying Bet</h3>

      <p>
        Place a real-money <span className="lay">lay</span> bet <em>against</em> the same outcome at a betting exchange.
      </p>
      <h6 className="info">A <span className="lay">lay</span> bet is a bet that an outcome <em>will not</em> happen.</h6>
      <p>
        The result is a small <spank className="key">qualifying loss</spank> of about 10p to £1, which unlocks your free bet.
      </p>

      <h3>Step 3: Use Your Free Bet</h3>
      <p>
        Once your free bet is in your account, repeat the process. This time, your{" "}
        <span className="back">back</span> bet is placed using the free bet, and you <span className="lay">lay</span> it again on the exchange.
      </p>
      <p>
        Because the stake isn’t yours this time, you <strong>keep most of the winnings</strong> as profit — typically around{" "}
        <strong>70–80% of the free bet value</strong>. For example, a £30 free bet can turn into{" "}
        <strong>£21–£24 profit</strong>.
      </p>

      <CTALarge 
        to="/calculators/lay-stake-calculator" 
        text="Go to the Matched Betting Calculator" 
        icon={Calculator} 
      />

      <h2>What You Need to Get Started</h2>
      <ul>
        <li>A computer or smartphone</li>
        <li>A UK debit card</li>
        <li>At least <strong>£50 starting bankroll</strong></li>
        <li>ID for account verification (passport or driving licence)</li>
        <li>
          A{" "}
          <Tooltip content="A tool that calculates the optimal stakes for your bets">matched betting calculator</Tooltip> (we have everything you need <Link to="/calculators">here</Link>)
        </li>
        
        <li>
          Optional: an{" "}
          <Tooltip content="A tool to help compare odds between bookmakers and exchanges">oddsmatcher tool</Tooltip> to find close matches between odds. We recommend OddsMonkey – <Link to="/guides/oddsmonkey-guide">here's why</Link>.
        </li>
      </ul>
      <div className="example-container">
              <h4>Example: Turn £10 into £28 Profit</h4>
              <p>
                Let’s say <Tooltip content="Example bookmaker">Betfred</Tooltip> offers “Bet £10, Get £40 in free bets”.
              </p>
              <ol>
                <li>
                  You <span className="back">back</span> Arsenal £10 to win at Betfred.
                </li>
                <li>
                  You <span className="lay">lay</span> Arsenal on{" "}
                  <Tooltip content="A popular betting exchange">Smarkets</Tooltip>.
                </li>
                <li>Your <spank className="key">qualifying loss</spank> is 15p this time.</li>
                <li>You receive your £40 in free bets (usually split into 4 x £10).</li>
                <li>
                  For each free bet:
                  <ul>
                    <li>You <span className="back">back</span> a <Tooltip content="Your stake isn't usually returned if you win a free bet, so high odds are important.">high-odds selection</Tooltip> (e.g. odds of 6.0)</li>
                    <li>You <span className="lay">lay</span> the same selection at the exchange</li>
                    <li>You lock in around £7–£8 profit per free bet</li>
                  </ul>
                </li>
              </ol>
      <p>
        After using all four, you’re left with around <strong>£28–£32 profit</strong> — and that’s just from one offer.
      </p>
             
            </div>

      <h2>How Much Can You Make from Matched Betting?</h2>
      <p>
        <strong>From sign-up offers:</strong> Expect around <strong>£500–£900 profit</strong>
        <br />
        <strong>From reload offers:</strong> Ongoing offers can bring <strong>£300–£1,000+ per month</strong> if you stay active
        <br />
        <img src={prof1} alt="matched betting profits: how much can you earn risk-free?" />
      </p>
      <BannerCTA 
        to="/guides/how-much-money-matched-betting" 
        text="Read our guide on how much you can realistically earn." 
        icon={ArrowUpRight} 
      />
      <ScrollModal scrollContainerRef={scrollRef} props={{ctaKey: "om2"}} />

      <h2>Is Matched Betting Legal?</h2>
      <p>
        Yes — matched betting is 100% legal in the UK. You’re simply using promotions in a clever, calculated way. It’s also{" "}
        <strong>tax-free</strong> because winnings from gambling aren’t taxed under UK law — even if they’re risk-free.
      </p>

      <h2>Is There Any Risk?</h2>
      <h6 className="info">Done perfectly, the maths guarantees that matched betting <em>would be risk-free</em>.</h6>
      <h6 className="risk"> The problem? Humans aren't perfect.</h6>
      <p>Some things that can go wrong include:</p>
        <ul>
          <li>You enter the wrong stake or odds.</li>
          <li>You forget to place one of the bets.</li>
          <li>You don't check there's enough <Tooltip content={"Money available in the market to match your bet"}>liquidity</Tooltip> at the exchange.</li>
          <li>You bet on the wrong event or outcome.</li>
        </ul>
      <p>
        The key is to <strong>go slow</strong> to start with, <strong>use a calculator</strong>, and <strong>double-check everything</strong> before confirming a bet.
      </p>
      <BannerCTA 
        to="/guides/matched-betting-risk-free-guide" 
        text="Read about risks when matched betting" 
        icon={ArrowUpRight} 
      />
      <h2>Why Isn't Everyone Matched Betting?</h2>
  <p>Matched betting might sound like an easy win, but here are some honest reasons why it isn’t for everyone:</p>
  <ul>
    <li><strong>Time Commitment:</strong> It’s not a “set-it-and-forget-it” method. Up to a point, returns are proportional to the time you invest.</li>
    <li><strong>Learning Curve:</strong> Understanding the strategies and managing the risks takes practice. The process can seem complex at first, and there’s a learning phase before you feel comfortable.</li>
    <li><strong>"Too Good to Be True:"</strong> The promise of making money with minimal risk might raise eyebrows. Many people are understandably skeptical when something appears that easy.</li>
    <li><strong>Worries About Gambling:</strong> Despite its risk-free nature when done correctly, some are hesitant because it still involves placing bets, and that association with gambling can be a turn-off.</li>
    <li><strong>Risk of getting hooked:</strong> It's crucial not to let matched betting become gambling. If you're worried about gambling, <a href="https://www.gambleaware.org/advice/for-your-gambling/" target="_blank" rel="noreferrer">get advice from GambleAware</a>.</li>
    <li><strong>Practical Challenges:</strong> Handling multiple betting accounts, keeping track of various promotions, and dealing with the constant updates in terms and conditions can be overwhelming.</li>
  </ul>
  <p>These challenges explain why everyone's not doing it. Each persons' circumstances ultimately determine if it’s the right fit.</p>

      <h2>Ready to Start?</h2>
      <p>
        If you’ve read this far, you’re already ahead of most. Matched betting is a brilliant way to make extra money in your spare time, especially since:
      </p>
      <ul>
        <li>No special skills are needed</li>
        <li>No gambling or guessing is involved</li>
        <li>The time commitment is flexible</li>
      </ul>
      <p>
        Start with one offer, take your time, and you'll quickly build confidence and profit.
      </p>


      <BannerCTA 
        to="/calculators/lay-stake-calculator" 
        text="Try the Matched Betting Calculator" 
        icon={ArrowUpRight} 
      />

      <h2>🔎 FAQs</h2>
      <h6 className="key-info">Do I need to know about sports?</h6>
      <p>Nope! Matched betting is about maths, not sports knowledge.</p>
      <h6 className="key-info">Can I lose money?</h6>
      <p>
        Not if you follow the steps and double-check your bets. Human error is the main risk.
      </p>
      <h6 className="key-info">Do bookies mind?</h6>
      <p>
        Bookmakers offer these deals to attract customers — you’re simply playing smart. Over time, some may limit your account (called “gubbing”), but by then you've already made your money.
      </p>
      <h6 className="key-info">How long does it take?</h6>
      <p>
        You can place your first matched bet in about 30–45 minutes. With practice, offers take just 2-5 minutes each.
      </p>

      <h2>🏁 Final Thoughts</h2>
      <p>
        Matched betting is one of the safest, simplest, and most proven ways to make <strong>tax-free money online in the UK</strong>. You don’t need any gambling experience or complex tools — just patience, a clear process, and the willingness to learn.
      </p>
      <p>
        💬 If you're ready to begin, let's solidify the basics...
      </p>
      <BannerCTA 
        to="/get-started/step1" 
        text="Get Started and Find Your First Offers" 
        icon={ArrowUpRight} 
      />
    </div>
  );
};

export default MatchedBettingGuide;
